import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Search, Building2, CheckCircle2, Pencil, Loader2, Users, MapPin, Phone, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RevisaoEAceiteEtapa } from '@/components/RevisaoEAceiteEtapa';
import { toast } from 'sonner';
import { CustomerData } from '@/services/customerService';
import { cnpjService, CnpjResponse, formatCnpj, formatPhone } from '@/services/cnpjService';
import { ResponsavelData, identificarSociosAdministradores, socioToResponsavel } from '@/services/conexaService';
import { isWhatsappBrValido, formatTelefoneBr } from '@/services/adhesionService';

// v1.5.1 do termo — contatos além do líder operacional / financeiro principal.
interface ContatoAdicional {
  nome: string;
  cargo: string;
  email: string;
  celular: string;
}
const contatoVazio = (): ContatoAdicional => ({ nome: '', cargo: '', email: '', celular: '' });
const isEmailValido = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || '').trim());

/** Lista editável de contatos adicionais (operacionais ou financeiros). */
function ContatosAdicionaisEditor({
  titulo,
  rotuloTelefone,
  contatos,
  onChange,
  className,
}: {
  titulo: string;
  rotuloTelefone: string;
  contatos: ContatoAdicional[];
  onChange: (next: ContatoAdicional[]) => void;
  className: string;
}) {
  const set = (i: number, patch: Partial<ContatoAdicional>) =>
    onChange(contatos.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  return (
    <div className="space-y-3">
      {contatos.map((c, i) => (
        <div key={i} className={`${className} rounded-lg p-4 space-y-3`}>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-brand-azul/80">{titulo} {i + 1}</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onChange(contatos.filter((_, idx) => idx !== i))}
              className="text-red-600 hover:text-red-700 h-7 px-2"
            >
              Remover
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-brand-azul/60">Nome Completo *</Label>
              <Input value={c.nome} onChange={(e) => set(i, { nome: e.target.value })} className="text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-brand-azul/60">Cargo</Label>
              <Input value={c.cargo} onChange={(e) => set(i, { cargo: e.target.value })} className="text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-brand-azul/60">Email *</Label>
              <Input type="email" value={c.email} onChange={(e) => set(i, { email: e.target.value })} className="text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-brand-azul/60">{rotuloTelefone} *</Label>
              <Input
                value={c.celular}
                onChange={(e) => set(i, { celular: formatTelefoneBr(e.target.value) })}
                placeholder="(00) 00000-0000"
                inputMode="tel"
                className="text-sm"
              />
            </div>
          </div>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onChange([...contatos, contatoVazio()])}
        className="text-brand-roxo border-brand-roxo/40"
      >
        + Adicionar {titulo.toLowerCase()}
      </Button>
    </div>
  );
}

// FLUXO ÚNICO DE ADESÃO POR TOKEN.
// O fluxo legado (cadastro orgânico → Asaas/Conexa) foi ELIMINADO em 2026-06-25.
// Motivo: a leitura anônima do token falhava e a página degradava silenciosamente
// pro fluxo antigo, mostrando "Cadastro concluído" sem efetivar a adesão
// (incidente São Valentin). Agora o token é o único caminho: sem token válido a
// página mostra erro e não renderiza formulário.

export default function RegistroCliente() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedPlan = location.state?.selectedPlan || 'Advanced';
  const customerId = location.state?.customerId;
  const isUpdate = location.state?.isUpdate || false;

  // Token from URL (generated by Sales Intelligence CloseDealModal)
  const tokenParam = searchParams.get('token');
  const [tokenData, setTokenData] = useState<any>(null);
  const [tokenLoading, setTokenLoading] = useState(true);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const tokenLoadedRef = useRef(false);

  const [isLoading, setIsLoading] = useState(false);
  const [customerData, setCustomerData] = useState<CustomerData>({
    name: '',
    email: '',
    cpfCnpj: '',
    phone: '',
    postalCode: '',
    address: '',
    addressNumber: '',
    complement: '',
    province: '',
    city: ''
  });
  // CNPJ lookup states
  const [cnpjInput, setCnpjInput] = useState('');
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [cnpjData, setCnpjData] = useState<CnpjResponse | null>(null);
  const [cnpjApproved, setCnpjApproved] = useState(false);
  const [cnpjEditMode, setCnpjEditMode] = useState(false);

  // Etapa atual do cadastro:
  //   1=CNPJ, 2=Dados Empresa, 3=Contatos (operacional + financeiro)
  //   4=Termo de Adesão (aceite limpo — sem opções extras)
  // O contrato formal opcional virou CTA em /cadastro-sucesso →
  // /contrato-formal/:adhesionId (página dedicada). Decisão tomada em
  // 2026-06-05 — ver docs/frontend-cta-contrato-formal-no-sucesso.md
  const [etapaAtual, setEtapaAtual] = useState<1 | 2 | 3 | 4>(1);

  // Detecta se o token traz proposta Dgenny completa (num_assistentes, etc).
  // Se sim, o fluxo termina na Etapa 4 com aceite de termo (cria Adhesion no
  // cs-dgenny). Se não (link orgânico ou token antigo), mantém fluxo legacy
  // (Asaas + Conexa direto via handleSubmit).
  // Modo ADITIVO: link de contratação de recurso adicional (tipo_adesao
  // 'aditivo'). Não traz num_assistentes — o termo é o de recurso adicional e
  // o CNPJ contratante já foi definido pelo vendedor na proposta.
  const isAditivo = tokenData?.deal_terms?.tipo_adesao === 'aditivo';
  const hasDgennyProposta = !!(
    tokenData?.deal_terms &&
    (isAditivo || typeof tokenData.deal_terms.num_assistentes === 'number')
  );

  // Responsáveis (sócios administradores + legal + financeiro + operacional)
  const [responsaveis, setResponsaveis] = useState<ResponsavelData[]>([]);
  
  const [responsavelFinanceiro, setResponsavelFinanceiro] = useState<ResponsavelData>({
    nome: '',
    cpf: '',
    email: '',
    celular: '',
    cargo: 'Responsável Financeiro',
    isSocio: false,
    isFinanceiro: true,
    isAdministrador: false,
    assinaContrato: false,
    testemunhaContrato: true, // Marcado por padrão
  });
  const [responsavelOperacional, setResponsavelOperacional] = useState<ResponsavelData>({
    nome: '',
    cpf: '',
    email: '',
    celular: '',
    cargo: 'Responsável Operacional',
    isSocio: false,
    isFinanceiro: false,
    isAdministrador: false,
    isOperacional: true,
    testemunhaContrato: false,
  });
  // v1.5.1 — contatos adicionais indicados pelo cliente na Etapa 3.
  const [operacionaisAdicionais, setOperacionaisAdicionais] = useState<ContatoAdicional[]>([]);
  const [financeirosAdicionais, setFinanceirosAdicionais] = useState<ContatoAdicional[]>([]);
  const [etapa3Erro, setEtapa3Erro] = useState<string | null>(null);

  // Gate da Etapa 3: o `required` dos inputs não vale (não há <form>), então
  // valida aqui. Financeiro precisa de WhatsApp válido (o servidor também
  // rejeita — 400 'whatsapp inválido').
  const validarEtapa3 = (): string | null => {
    const fin = responsavelFinanceiro;
    if (!fin.nome.trim()) return 'Informe o nome do responsável financeiro.';
    if (!isEmailValido(fin.email)) return 'Informe um e-mail válido do responsável financeiro.';
    if (!isWhatsappBrValido(fin.celular)) return 'Informe um WhatsApp válido (DDD + número) do responsável financeiro.';
    const op = responsavelOperacional;
    if (!op.nome.trim()) return 'Informe o nome do responsável operacional.';
    if (!isEmailValido(op.email)) return 'Informe um e-mail válido do responsável operacional.';
    if (op.celular.replace(/\D/g, '').length < 10) return 'Informe um telefone válido (DDD + número) do responsável operacional.';
    for (let i = 0; i < financeirosAdicionais.length; i++) {
      const c = financeirosAdicionais[i];
      if (!c.nome.trim() || !isEmailValido(c.email)) return `Contato financeiro ${i + 1}: informe nome e e-mail válido.`;
      if (!isWhatsappBrValido(c.celular)) return `Contato financeiro ${i + 1}: informe um WhatsApp válido (DDD + número).`;
    }
    for (let i = 0; i < operacionaisAdicionais.length; i++) {
      const c = operacionaisAdicionais[i];
      if (!c.nome.trim() || !isEmailValido(c.email)) return `Contato operacional ${i + 1}: informe nome e e-mail válido.`;
      if (c.celular.replace(/\D/g, '').length < 10) return `Contato operacional ${i + 1}: informe um telefone válido (DDD + número).`;
    }
    return null;
  };
  
  // Checkboxes "copiar dados" (inertes no fluxo de adesão por token — mantidos
  // apenas porque os campos compartilhados de financeiro/operacional referenciam
  // o estado em `disabled`/opacity; permanecem sempre false).
  const [copiarDadosLegalParaFinanceiro, setCopiarDadosLegalParaFinanceiro] = useState(false);
  const [copiarDadosLegalParaOperacional, setCopiarDadosLegalParaOperacional] = useState(false);
  const [copiarDadosFinanceiroParaOperacional, setCopiarDadosFinanceiroParaOperacional] = useState(false);

  // ID do cliente criado no Conexa (para vincular pessoas)
  const [conexaCustomerId, setConexaCustomerId] = useState<number | null>(null);
  
  // Carregar dados do cliente se estivermos no modo de atualização
  useEffect(() => {
    const loadCustomerData = async () => {
      if (isUpdate && customerId) {
        setIsLoading(true);
        try {
          // Tentar recuperar dados do sessionStorage primeiro
          const storedData = sessionStorage.getItem('checkoutData');
          if (storedData) {
            const parsedData = JSON.parse(storedData);
            if (parsedData.customerData) {
              setCustomerData(parsedData.customerData);
              setIsLoading(false);
              return;
            }
          }
          
          // Se não encontrar no sessionStorage, buscar da API
          // Nota: Você precisaria implementar um método getCustomer no asaasService
          // const customerResponse = await asaasService.getCustomer(customerId);
          // setCustomerData({
          //   name: customerResponse.name,
          //   email: customerResponse.email,
          //   cpfCnpj: customerResponse.cpfCnpj,
          //   phone: customerResponse.phone || '',
          //   postalCode: customerResponse.postalCode || '',
          //   address: customerResponse.address || '',
          //   addressNumber: customerResponse.addressNumber || '',
          //   complement: customerResponse.complement || '',
          //   province: customerResponse.province || '',
          //   city: customerResponse.city || ''
          // });
          
          toast.info('Por favor, complete os dados de endereço para continuar.');
        } catch (error) {
          console.error('Erro ao carregar dados do cliente:', error);
          toast.error('Erro ao carregar dados do cliente. Por favor, preencha o formulário novamente.');
        } finally {
          setIsLoading(false);
        }
      }
    };
    
    loadCustomerData();
  }, [isUpdate, customerId]);

  // Carrega o token de adesão do Firestore. O token é OBRIGATÓRIO: sem ele (ou se
  // a leitura falhar / expirar / já estar concluído) a página mostra ERRO e NÃO
  // renderiza o formulário. Nunca mais degrada para um cadastro silencioso.
  // A leitura é anônima e depende da regra pública `allow get` em
  // registration_tokens (cs-dgenny/firestore.rules).
  useEffect(() => {
    if (tokenLoadedRef.current) return;
    tokenLoadedRef.current = true;

    if (!tokenParam) {
      setTokenLoading(false);
      setTokenError('Link de adesão inválido. Solicite o link de contratação ao seu contato na dgenny.');
      return;
    }

    (async () => {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/integrations/firebase/client');
        const snap = await getDoc(doc(db, 'registration_tokens', tokenParam));
        if (!snap.exists()) {
          setTokenError('Link inválido ou expirado. Solicite um novo ao seu contato na dgenny.');
          return;
        }
        const data = snap.data();
        if (data.expires_at?.toDate && data.expires_at.toDate() < new Date()) {
          setTokenError('Este link expirou. Solicite um novo ao seu contato na dgenny.');
          return;
        }
        if (data.status === 'completed') {
          setTokenError('Esta adesão já foi concluída. Se precisar de ajuda, fale com compre.ai@dgenny.com.br.');
          return;
        }
        const dt = data.deal_terms;
        const tokenAditivo = dt?.tipo_adesao === 'aditivo';
        if (tokenAditivo) {
          // Aditivo não tem num_assistentes: exige recurso nomeado e valor mensal.
          if (!(Number(dt.valor_mensalidade_centavos) > 0) || !dt.produto_nome) {
            setTokenError('Link de recurso adicional incompleto (sem recurso ou valor). Solicite um novo ao seu contato na dgenny.');
            return;
          }
        } else if (!dt || typeof dt.num_assistentes !== 'number') {
          setTokenError('Link de adesão incompleto (sem proposta vinculada). Solicite um novo ao seu contato na dgenny.');
          return;
        }
        setTokenData(data);
        if (data.empresa_hint?.cnpj) {
          setCnpjInput(data.empresa_hint.cnpj);
        }
        toast.success(
          tokenAditivo
            ? 'Recurso adicional carregado. Confirme os dados da empresa contratante para concluir.'
            : 'Proposta carregada. Confirme os dados da empresa para concluir a adesão.',
        );
      } catch (err) {
        console.error('Erro ao carregar token de adesão:', err);
        setTokenError('Não foi possível carregar o link de adesão. Verifique sua conexão e recarregue a página. Se persistir, fale com seu contato na dgenny.');
      } finally {
        setTokenLoading(false);
      }
    })();
  }, [tokenParam]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCustomerData(prev => ({ ...prev, [name]: value }));
  };

  // CNPJ Lookup
  const handleCnpjLookup = useCallback(async () => {
    const cleanCnpj = cnpjInput.replace(/[^\d]/g, '');
    if (cleanCnpj.length !== 14) {
      toast.error('Informe um CNPJ válido com 14 dígitos.');
      return;
    }

    setCnpjLoading(true);
    setCnpjData(null);
    setCnpjApproved(false);
    setCnpjEditMode(false);

    try {
      const data = await cnpjService.lookup(cleanCnpj);
      setCnpjData(data);
      toast.success('CNPJ encontrado! Revise os dados abaixo.');
    } catch {
      // toast already shown by service
    } finally {
      setCnpjLoading(false);
    }
  }, [cnpjInput]);

  // Auto-trigger CNPJ lookup when cnpjInput is set from token
  useEffect(() => {
    if (tokenData && cnpjInput && cnpjInput.replace(/\D/g, '').length === 14 && !cnpjData && !cnpjLoading) {
      handleCnpjLookup();
    }
  }, [tokenData, cnpjInput, cnpjData, cnpjLoading, handleCnpjLookup]);

  // Preenche o formulário com dados da API do CNPJ
  const fillFormFromCnpj = useCallback((data: CnpjResponse) => {
    const est = data.estabelecimento;
    const phone = est.ddd1 && est.telefone1 ? `${est.ddd1}${est.telefone1}` : '';
    const logradouro = est.tipo_logradouro
      ? `${est.tipo_logradouro} ${est.logradouro}`
      : est.logradouro;

    setCustomerData(prev => ({
      ...prev,
      name: data.razao_social || prev.name,
      email: est.email || prev.email,
      cpfCnpj: est.cnpj || prev.cpfCnpj,
      phone: phone || prev.phone,
      postalCode: est.cep || prev.postalCode,
      address: logradouro || prev.address,
      addressNumber: est.numero || prev.addressNumber,
      complement: est.complemento || prev.complement,
      province: est.bairro || prev.province,
      city: est.cidade?.nome || prev.city,
    }));
  }, []);

  // Pré-preencher responsáveis com sócios administradores do CNPJ
  const preencherResponsaveisFromCnpj = useCallback((data: CnpjResponse) => {
    if (!data.socios || data.socios.length === 0) {
      setResponsaveis([]);
      return;
    }
    // Identificar sócios administradores
    const admins = identificarSociosAdministradores(data.socios);
    if (admins.length > 0) {
      setResponsaveis(admins.map(socioToResponsavel));
    } else {
      // Se não houver administradores identificados, usar todos os sócios
      setResponsaveis(data.socios.slice(0, 3).map(socioToResponsavel));
    }
  }, []);

  // Aprovar dados do CNPJ
  const handleApproveCnpj = useCallback(() => {
    if (!cnpjData) return;
    fillFormFromCnpj(cnpjData);
    preencherResponsaveisFromCnpj(cnpjData);
    setCnpjApproved(true);
    setCnpjEditMode(false);
    setEtapaAtual(2);
    toast.success('Dados da empresa aprovados e preenchidos no formulário.');
  }, [cnpjData, fillFormFromCnpj, preencherResponsaveisFromCnpj]);

  // Entrar em modo edição
  const handleEditCnpj = useCallback(() => {
    if (!cnpjData) return;
    fillFormFromCnpj(cnpjData);
    preencherResponsaveisFromCnpj(cnpjData);
    setCnpjApproved(true);
    setCnpjEditMode(true);
    setEtapaAtual(2);
    toast.info('Dados preenchidos. Edite os campos que desejar.');
  }, [cnpjData, fillFormFromCnpj, preencherResponsaveisFromCnpj]);

  // Nova consulta de CNPJ
  const handleResetCnpj = useCallback(() => {
    setCnpjData(null);
    setCnpjApproved(false);
    setCnpjEditMode(false);
    setCnpjInput('');
    setEtapaAtual(1);
    setResponsaveis([]);
    setResponsavelFinanceiro({
      nome: '',
      cpf: '',
      email: '',
      celular: '',
      cargo: 'Responsável Financeiro',
      isSocio: false,
      isFinanceiro: true,
      isAdministrador: false,
    });
    setConexaCustomerId(null);
    sessionStorage.removeItem('cnpj_socios');
    sessionStorage.removeItem('adhesionId');
    sessionStorage.removeItem('companyId');
  }, []);

  // Persiste sócios do CNPJ pra sobreviver a F5 na Etapa 5. Sempre que
  // `responsaveis` mudar (foi populado pela aprovação do CNPJ), grava.
  useEffect(() => {
    if (responsaveis.length > 0) {
      try {
        sessionStorage.setItem('cnpj_socios', JSON.stringify(responsaveis));
      } catch {
        // sessionStorage cheio ou indisponível — falha silenciosa
      }
    }
  }, [responsaveis]);

  // Restaura sócios do sessionStorage no mount, se o state estiver vazio
  // (cobre o caso de F5 entre Etapa 4 e 5).
  useEffect(() => {
    if (responsaveis.length === 0) {
      try {
        const raw = sessionStorage.getItem('cnpj_socios');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setResponsaveis(parsed);
          }
        }
      } catch {
        // ignore
      }
    }
    // intencionalmente roda só uma vez no mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Formata o input do CNPJ enquanto digita
  const handleCnpjInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^\d]/g, '');
    if (raw.length <= 14) {
      setCnpjInput(formatCnpj(raw.length === 14 ? raw : raw));
    }
  };

  // [REMOVIDO 2026-06-25] handleSubmit + validateForm (fluxo legado Asaas/Conexa/
  // management-intake/webhook n8n + navigate incondicional para /cadastro-sucesso).
  // O fluxo de adesão por token é o único caminho — ver RevisaoEAceiteEtapa
  // (Etapa 4) → createAdhesion.

  // Token é OBRIGATÓRIO. Enquanto carrega, mostra spinner; se inválido/expirado/
  // já concluído ou se a leitura falhar, mostra erro e NÃO renderiza o formulário.
  if (tokenLoading || tokenError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-rosa/10 via-white to-brand-roxo/5">
        <header className="py-4 bg-white border-b border-border relative">
          <div className="container px-4 mx-auto">
            <div className="flex items-center">
              <img src="/brand/logo-secundario.svg" alt="dgenny" className="h-9" />
            </div>
          </div>
          <div className="absolute inset-x-0 bottom-0 h-[3px] bg-gradient-to-r from-brand-rosa via-brand-roxo to-brand-azul" aria-hidden />
        </header>
        <main className="container px-4 mx-auto py-16 md:py-24">
          <div className="max-w-lg mx-auto">
            <Card className="p-10 border-border shadow-sm text-center rounded-3xl">
              {tokenLoading ? (
                <div className="flex flex-col items-center gap-4">
                  <Loader2 size={40} className="animate-spin text-brand-roxo" />
                  <p className="text-brand-azul/70">Carregando sua proposta de adesão…</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-4">
                  <div className="w-16 h-16 bg-brand-rosa/20 rounded-full flex items-center justify-center">
                    <Building2 size={30} className="text-brand-roxo" />
                  </div>
                  <h1 className="font-display text-2xl font-extrabold text-brand-azul">Link de adesão indisponível</h1>
                  <p className="text-brand-azul/70 leading-relaxed">{tokenError}</p>
                  <div className="bg-muted/60 rounded-2xl p-4 mt-2 w-full">
                    <p className="text-sm text-brand-azul/60">Precisa de ajuda?</p>
                    <p className="text-brand-azul font-semibold mt-1">compre.ai@dgenny.com.br</p>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-rosa/10 via-white to-brand-roxo/5">
      <header className="py-4 bg-white border-b border-border relative">
        <div className="container px-4 mx-auto">
          <div className="flex items-center">
            <img src="/brand/logo-secundario.svg" alt="dgenny" className="h-9" />
          </div>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-[3px] bg-gradient-to-r from-brand-rosa via-brand-roxo to-brand-azul" aria-hidden />
      </header>

      <main className="container px-4 mx-auto py-12">
        <div className="max-w-3xl mx-auto">
          <div className="mb-10 text-center">
            <span className="inline-block text-brand-roxo font-semibold text-sm uppercase tracking-wider mb-3">
              {isAditivo ? 'Contratação de recurso adicional' : 'Contratação'}
            </span>
            <h1 className="font-display text-3xl md:text-4xl font-extrabold text-brand-azul mb-3">
              {isAditivo ? 'Contratação de recurso adicional' : 'Cadastro da empresa'}
            </h1>
            <p className="text-brand-azul/70 mb-8 max-w-lg mx-auto">
              {isAditivo
                ? 'Confirme os dados da empresa contratante, os contatos e aceite o termo do recurso adicional.'
                : 'Informe o CNPJ para iniciar sua contratação. A dgenny cuida do resto.'}
            </p>

            {/* Indicador de Etapas */}
            <div className="flex items-center justify-center gap-2">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition ${etapaAtual >= 1 ? 'bg-brand-roxo text-white shadow-lg shadow-brand-roxo/20' : 'bg-white border border-border text-brand-azul/50'}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${etapaAtual >= 1 ? 'bg-white/25' : 'bg-brand-azul/5'}`}>1</span>
                CNPJ
              </div>
              <div className={`w-10 h-0.5 ${etapaAtual >= 2 ? 'bg-brand-roxo' : 'bg-border'}`} />
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition ${etapaAtual >= 2 ? 'bg-brand-roxo text-white shadow-lg shadow-brand-roxo/20' : 'bg-white border border-border text-brand-azul/50'}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${etapaAtual >= 2 ? 'bg-white/25' : 'bg-brand-azul/5'}`}>2</span>
                Empresa
              </div>
              <div className={`w-10 h-0.5 ${etapaAtual >= 3 ? 'bg-brand-roxo' : 'bg-border'}`} />
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition ${etapaAtual >= 3 ? 'bg-brand-roxo text-white shadow-lg shadow-brand-roxo/20' : 'bg-white border border-border text-brand-azul/50'}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${etapaAtual >= 3 ? 'bg-white/25' : 'bg-brand-azul/5'}`}>3</span>
                {hasDgennyProposta ? 'Contatos' : 'Responsáveis'}
              </div>
              {hasDgennyProposta && (
                <>
                  <div className={`w-10 h-0.5 ${etapaAtual >= 4 ? 'bg-brand-roxo' : 'bg-border'}`} />
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition ${etapaAtual >= 4 ? 'bg-brand-roxo text-white shadow-lg shadow-brand-roxo/20' : 'bg-white border border-border text-brand-azul/50'}`}>
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${etapaAtual >= 4 ? 'bg-white/25' : 'bg-brand-azul/5'}`}>4</span>
                    Termo
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ========== PASSO 1: Consulta de CNPJ ========== */}
          <Card className="p-8 shadow-lg border-border rounded-2xl mb-6 relative overflow-hidden">
            <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-brand-roxo to-brand-rosa" aria-hidden />
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-brand-roxo/10 flex items-center justify-center shrink-0">
                <Building2 size={20} className="text-brand-roxo" />
              </div>
              <h2 className="font-display text-xl font-bold text-brand-azul">1. Dados da Empresa</h2>
            </div>
            <p className="text-brand-azul/70 text-sm mb-6">
              {isAditivo
                ? 'O CNPJ contratante (que paga este recurso) foi definido na proposta. Confira os dados abaixo.'
                : 'Informe o CNPJ da empresa para buscar os dados automaticamente.'}
            </p>

            {/* CNPJ Input + Buscar — no aditivo o CNPJ vem travado da proposta */}
            {!cnpjData && (
              <div className="flex gap-3 items-end">
                <div className="flex-1 space-y-2">
                  <Label htmlFor="cnpjInput">CNPJ *</Label>
                  <Input
                    id="cnpjInput"
                    value={cnpjInput}
                    onChange={handleCnpjInputChange}
                    placeholder="00.000.000/0000-00"
                    disabled={cnpjLoading}
                    readOnly={isAditivo}
                    onClick={() => { if (isAditivo) toast.info('O CNPJ contratante foi definido na proposta. Para alterar, fale com seu contato na dgenny.'); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCnpjLookup(); } }}
                  />
                </div>
                <Button
                  type="button"
                  onClick={handleCnpjLookup}
                  disabled={cnpjLoading || cnpjInput.replace(/[^\d]/g, '').length !== 14}
                  className="bg-brand-roxo text-white hover:bg-brand-roxo/90 rounded-full font-semibold px-5 flex items-center gap-2 disabled:bg-brand-roxo/40 disabled:text-white"
                >
                  {cnpjLoading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                  {cnpjLoading ? 'Buscando...' : 'Consultar CNPJ'}
                </Button>
              </div>
            )}

            {/* Resultado da Consulta CNPJ */}
            {cnpjData && !cnpjApproved && (
              <div className="mt-4 space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 space-y-4">
                  <div className="flex items-center gap-2 text-blue-800 font-semibold text-base">
                    <Building2 size={18} />
                    Dados encontrados — Revise e aprove ou edite
                  </div>

                  {/* Dados Principais */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-brand-azul/60">CNPJ:</span>{' '}
                      <span className="font-medium">{formatCnpj(cnpjData.estabelecimento.cnpj)}</span>
                    </div>
                    <div>
                      <span className="text-brand-azul/60">Razão Social:</span>{' '}
                      <span className="font-medium">{cnpjData.razao_social}</span>
                    </div>
                    <div>
                      <span className="text-brand-azul/60">Nome Fantasia:</span>{' '}
                      <span className="font-medium">{cnpjData.estabelecimento.nome_fantasia || '—'}</span>
                    </div>
                    <div>
                      <span className="text-brand-azul/60">Situação Cadastral:</span>{' '}
                      <span className={`font-medium ${cnpjData.estabelecimento.situacao_cadastral === 'Ativa' ? 'text-green-700' : 'text-red-700'}`}>
                        {cnpjData.estabelecimento.situacao_cadastral}
                      </span>
                    </div>
                    <div>
                      <span className="text-brand-azul/60">Natureza Jurídica:</span>{' '}
                      <span className="font-medium">{cnpjData.natureza_juridica?.descricao || '—'}</span>
                    </div>
                    <div>
                      <span className="text-brand-azul/60">Porte:</span>{' '}
                      <span className="font-medium">{cnpjData.porte?.descricao || '—'}</span>
                    </div>
                    <div>
                      <span className="text-brand-azul/60">Capital Social:</span>{' '}
                      <span className="font-medium">
                        {cnpjData.capital_social
                          ? Number(cnpjData.capital_social).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                          : '—'}
                      </span>
                    </div>
                    <div>
                      <span className="text-brand-azul/60">Início Atividade:</span>{' '}
                      <span className="font-medium">
                        {cnpjData.estabelecimento.data_inicio_atividade
                          ? new Date(cnpjData.estabelecimento.data_inicio_atividade + 'T00:00:00').toLocaleDateString('pt-BR')
                          : '—'}
                      </span>
                    </div>
                  </div>

                  {/* Atividade Principal */}
                  {cnpjData.estabelecimento.atividade_principal && (
                    <div className="text-sm">
                      <span className="text-brand-azul/60">Atividade Principal:</span>{' '}
                      <span className="font-medium">
                        {cnpjData.estabelecimento.atividade_principal.id} — {cnpjData.estabelecimento.atividade_principal.descricao}
                      </span>
                    </div>
                  )}

                  {/* Endereço */}
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin size={14} className="text-brand-azul/60 mt-0.5 shrink-0" />
                    <span>
                      {[
                        cnpjData.estabelecimento.tipo_logradouro,
                        cnpjData.estabelecimento.logradouro,
                        cnpjData.estabelecimento.numero && `nº ${cnpjData.estabelecimento.numero}`,
                        cnpjData.estabelecimento.complemento,
                        cnpjData.estabelecimento.bairro,
                        cnpjData.estabelecimento.cidade?.nome,
                        cnpjData.estabelecimento.estado?.sigla,
                        cnpjData.estabelecimento.cep && `CEP ${cnpjData.estabelecimento.cep}`,
                      ].filter(Boolean).join(', ')}
                    </span>
                  </div>

                  {/* Contato */}
                  <div className="flex flex-wrap gap-4 text-sm">
                    {cnpjData.estabelecimento.ddd1 && cnpjData.estabelecimento.telefone1 && (
                      <div className="flex items-center gap-1">
                        <Phone size={14} className="text-brand-azul/60" />
                        {formatPhone(cnpjData.estabelecimento.ddd1, cnpjData.estabelecimento.telefone1)}
                      </div>
                    )}
                    {cnpjData.estabelecimento.email && (
                      <div className="flex items-center gap-1">
                        <Mail size={14} className="text-brand-azul/60" />
                        {cnpjData.estabelecimento.email}
                      </div>
                    )}
                  </div>

                  {/* Sócios */}
                  {cnpjData.socios && cnpjData.socios.length > 0 && (
                    <div className="border-t border-blue-200 pt-3">
                      <div className="flex items-center gap-1 text-sm font-semibold text-blue-800 mb-2">
                        <Users size={14} />
                        Quadro Societário ({cnpjData.socios.length})
                      </div>
                      <div className="space-y-1 text-sm">
                        {cnpjData.socios.map((socio, idx) => (
                          <div key={idx} className="flex flex-wrap gap-x-4">
                            <span className="font-medium">{socio.nome}</span>
                            <span className="text-brand-azul/60">{socio.qualificacao_socio?.descricao}</span>
                            {socio.data_entrada && (
                              <span className="text-brand-azul/40 text-xs">
                                Desde {new Date(socio.data_entrada + 'T00:00:00').toLocaleDateString('pt-BR')}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Simples Nacional / MEI */}
                  {cnpjData.simples && (
                    <div className="flex flex-wrap gap-4 text-sm border-t border-blue-200 pt-3">
                      <div>
                        <span className="text-brand-azul/60">Simples Nacional:</span>{' '}
                        <span className="font-medium">{cnpjData.simples.simples === 'Sim' ? 'Optante' : 'Não optante'}</span>
                      </div>
                      <div>
                        <span className="text-brand-azul/60">MEI:</span>{' '}
                        <span className="font-medium">{cnpjData.simples.mei === 'Sim' ? 'Sim' : 'Não'}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Botões Aprovar / Editar / Nova Consulta */}
                <div className="flex flex-wrap gap-3">
                  <Button
                    type="button"
                    onClick={handleApproveCnpj}
                    className="bg-green-600 text-white hover:bg-green-700 flex items-center gap-2"
                  >
                    <CheckCircle2 size={16} />
                    Aprovar Dados
                  </Button>
                  <Button
                    type="button"
                    onClick={handleEditCnpj}
                    variant="outline"
                    className="border-brand-azul text-brand-azul hover:bg-brand-azul/5 flex items-center gap-2"
                  >
                    <Pencil size={16} />
                    Editar Dados
                  </Button>
                  {!isAditivo && (
                    <Button
                      type="button"
                      onClick={handleResetCnpj}
                      variant="ghost"
                      className="text-brand-azul/60 hover:text-brand-azul/80"
                    >
                      Nova Consulta
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* CNPJ Aprovado — resumo compacto */}
            {cnpjApproved && cnpjData && !cnpjEditMode && (
              <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="text-green-600 mt-0.5 shrink-0" />
                  <div className="text-sm space-y-1">
                    <p className="font-semibold text-green-800">Dados da empresa aprovados</p>
                    <p><span className="text-brand-azul/60">CNPJ:</span> {formatCnpj(cnpjData.estabelecimento.cnpj)}</p>
                    <p><span className="text-brand-azul/60">Razão Social:</span> {cnpjData.razao_social}</p>
                    {cnpjData.estabelecimento.nome_fantasia && (
                      <p><span className="text-brand-azul/60">Nome Fantasia:</span> {cnpjData.estabelecimento.nome_fantasia}</p>
                    )}
                  </div>
                </div>
                <Button
                  type="button"
                  onClick={handleResetCnpj}
                  variant="ghost"
                  size="sm"
                  disabled={isAditivo}
                  title={isAditivo ? 'O CNPJ contratante foi definido na proposta.' : undefined}
                  className="text-brand-azul/60 hover:text-brand-azul/80 shrink-0"
                >
                  Alterar CNPJ
                </Button>
              </div>
            )}
          </Card>

          {/* ========== PASSO 2: Dados Complementares (só aparece após aprovar/editar) ========== */}
          {cnpjApproved && (
            <Card className="p-8 shadow-lg border-border rounded-2xl mb-6 relative overflow-hidden"><div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-brand-roxo to-brand-rosa" aria-hidden />
              <form onSubmit={(e) => { e.preventDefault(); setEtapaAtual(3); }}>
                <div className="space-y-6">
                  <h2 className="font-display text-xl font-bold text-brand-azul border-b border-border pb-3 mb-2">2. Dados de Contato e Endereço</h2>
                  <p className="text-brand-azul/60 text-sm -mt-3">
                    {cnpjEditMode
                      ? 'Os dados foram pré-preenchidos. Edite os campos que desejar.'
                      : 'Campos preenchidos automaticamente. Confira e complete se necessário.'}
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Razão Social / Nome *</Label>
                      <Input 
                        id="name"
                        name="name"
                        value={customerData.name}
                        onChange={handleInputChange}
                        placeholder="Nome completo ou razão social"
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input 
                        id="email"
                        name="email"
                        type="email"
                        value={customerData.email}
                        onChange={handleInputChange}
                        placeholder="seu@email.com"
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="cpfCnpj">CNPJ *</Label>
                      <Input 
                        id="cpfCnpj"
                        name="cpfCnpj"
                        value={customerData.cpfCnpj}
                        onChange={handleInputChange}
                        placeholder="Somente números"
                        required
                        disabled={!cnpjEditMode}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="phone">Telefone *</Label>
                      <Input 
                        id="phone"
                        name="phone"
                        value={customerData.phone}
                        onChange={handleInputChange}
                        placeholder="(00) 00000-0000"
                        required
                      />
                    </div>
                  </div>
                  
                  <h3 className="text-lg font-semibold border-b pb-2 pt-2">Endereço</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="postalCode">CEP *</Label>
                      <Input 
                        id="postalCode"
                        name="postalCode"
                        value={customerData.postalCode}
                        onChange={handleInputChange}
                        placeholder="00000-000"
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="address">Logradouro *</Label>
                      <Input 
                        id="address"
                        name="address"
                        value={customerData.address}
                        onChange={handleInputChange}
                        placeholder="Rua, Avenida, etc."
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="addressNumber">Número *</Label>
                      <Input 
                        id="addressNumber"
                        name="addressNumber"
                        value={customerData.addressNumber}
                        onChange={handleInputChange}
                        placeholder="123"
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="complement">Complemento</Label>
                      <Input 
                        id="complement"
                        name="complement"
                        value={customerData.complement}
                        onChange={handleInputChange}
                        placeholder="Apto, Sala, etc."
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="province">Bairro *</Label>
                      <Input 
                        id="province"
                        name="province"
                        value={customerData.province}
                        onChange={handleInputChange}
                        placeholder="Seu bairro"
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="city">Cidade *</Label>
                      <Input 
                        id="city"
                        name="city"
                        value={customerData.city}
                        onChange={handleInputChange}
                        placeholder="Sua cidade"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-end pt-4">
                    <Button 
                      type="button"
                      onClick={() => setEtapaAtual(3)}
                      className="bg-brand-roxo hover:bg-brand-roxo/90 text-white py-2 px-6 rounded-lg font-medium flex items-center justify-center gap-2"
                    >
                      Próximo: Responsáveis
                      <ArrowRight size={16} />
                    </Button>
                  </div>
                </div>
              </form>
            </Card>
          )}

          {/* ========== PASSO 3: Responsáveis (sócios administradores + financeiro) ========== */}
          {etapaAtual === 3 && cnpjApproved && (
            <Card className="p-8 shadow-lg border-border rounded-2xl mb-6 relative overflow-hidden"><div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-brand-roxo to-brand-rosa" aria-hidden />
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="font-display text-xl font-bold text-brand-azul border-b border-border pb-3 mb-2 flex-1">
                    {hasDgennyProposta ? '3. Contatos Operacionais' : '3. Responsáveis'}
                  </h2>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setEtapaAtual(2)}
                    className="text-brand-azul/60 hover:text-brand-azul/80"
                  >
                    ← Voltar
                  </Button>
                </div>
                <p className="text-brand-azul/60 text-sm -mt-3">
                  {hasDgennyProposta
                    ? 'Informe os contatos operacional e financeiro da empresa. Representante legal e testemunha só são coletados se você optar por contrato formal após o aceite do termo.'
                    : 'Informe os dados dos responsáveis pela empresa. Os sócios administradores foram pré-preenchidos com base no CNPJ.'}
                </p>





                {/* Responsável Financeiro */}
                <div className="space-y-4 pt-4 border-t">
                  <h3 className="text-lg font-medium flex items-center gap-2">
                    <Mail size={18} className="text-brand-roxo" />
                    Responsável Financeiro
                  </h3>
                  <p className="text-sm text-brand-azul/60 -mt-2">
                    Informe os dados do responsável por assuntos financeiros e cobrança.
                  </p>

                  <div className={`bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-4 ${copiarDadosLegalParaFinanceiro ? 'opacity-60' : ''}`}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-brand-azul/60">Nome Completo *</Label>
                        <Input
                          value={responsavelFinanceiro.nome}
                          onChange={(e) => setResponsavelFinanceiro(prev => ({ ...prev, nome: e.target.value }))}
                          placeholder="Nome do responsável financeiro"
                          className="text-sm"
                          required
                          disabled={copiarDadosLegalParaFinanceiro}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-brand-azul/60">CPF</Label>
                        <Input
                          value={responsavelFinanceiro.cpf}
                          onChange={(e) => setResponsavelFinanceiro(prev => ({ ...prev, cpf: e.target.value }))}
                          placeholder="000.000.000-00"
                          className="text-sm"
                          disabled={copiarDadosLegalParaFinanceiro}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-brand-azul/60">Email *</Label>
                        <Input
                          type="email"
                          value={responsavelFinanceiro.email}
                          onChange={(e) => setResponsavelFinanceiro(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="financeiro@empresa.com"
                          className="text-sm"
                          required
                          disabled={copiarDadosLegalParaFinanceiro}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-brand-azul/60">WhatsApp *</Label>
                        <Input
                          value={responsavelFinanceiro.celular}
                          onChange={(e) => setResponsavelFinanceiro(prev => ({ ...prev, celular: formatTelefoneBr(e.target.value) }))}
                          placeholder="(00) 00000-0000"
                          inputMode="tel"
                          className="text-sm"
                          required
                          disabled={copiarDadosLegalParaFinanceiro}
                        />
                        <p className="text-[11px] text-brand-azul/50">Boletos e notas fiscais também são enviados por WhatsApp.</p>
                      </div>
                    </div>

                  </div>
                </div>

                <ContatosAdicionaisEditor
                  titulo="Outro contato financeiro"
                  rotuloTelefone="WhatsApp"
                  contatos={financeirosAdicionais}
                  onChange={setFinanceirosAdicionais}
                  className="bg-orange-50 border border-orange-200"
                />

                {/* Responsável Operacional */}
                <div className="space-y-4 pt-4 border-t">
                  <h3 className="text-lg font-medium flex items-center gap-2">
                    <Users size={18} className="text-green-600" />
                    Responsável Operacional
                  </h3>
                  <p className="text-sm text-brand-azul/60 -mt-2">
                    Informe os dados do responsável pela operação e uso do sistema.
                  </p>

                  <div className={`bg-green-50 border border-green-200 rounded-lg p-4 space-y-4 ${(copiarDadosLegalParaOperacional || copiarDadosFinanceiroParaOperacional) ? 'opacity-60' : ''}`}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-brand-azul/60">Nome Completo *</Label>
                        <Input
                          value={responsavelOperacional.nome}
                          onChange={(e) => setResponsavelOperacional(prev => ({ ...prev, nome: e.target.value }))}
                          placeholder="Nome do responsável operacional"
                          className="text-sm"
                          required
                          disabled={copiarDadosLegalParaOperacional || copiarDadosFinanceiroParaOperacional}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-brand-azul/60">CPF</Label>
                        <Input
                          value={responsavelOperacional.cpf}
                          onChange={(e) => setResponsavelOperacional(prev => ({ ...prev, cpf: e.target.value }))}
                          placeholder="000.000.000-00"
                          className="text-sm"
                          disabled={copiarDadosLegalParaOperacional || copiarDadosFinanceiroParaOperacional}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-brand-azul/60">Email *</Label>
                        <Input
                          type="email"
                          value={responsavelOperacional.email}
                          onChange={(e) => setResponsavelOperacional(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="operacional@empresa.com"
                          className="text-sm"
                          required
                          disabled={copiarDadosLegalParaOperacional || copiarDadosFinanceiroParaOperacional}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-brand-azul/60">Celular *</Label>
                        <Input
                          value={responsavelOperacional.celular}
                          onChange={(e) => setResponsavelOperacional(prev => ({ ...prev, celular: formatTelefoneBr(e.target.value) }))}
                          inputMode="tel"
                          placeholder="(00) 00000-0000"
                          className="text-sm"
                          required
                          disabled={copiarDadosLegalParaOperacional || copiarDadosFinanceiroParaOperacional}
                        />
                      </div>
                    </div>

                  </div>
                </div>



                <ContatosAdicionaisEditor
                  titulo="Outro contato operacional"
                  rotuloTelefone="Telefone"
                  contatos={operacionaisAdicionais}
                  onChange={setOperacionaisAdicionais}
                  className="bg-green-50 border border-green-200"
                />

                {etapa3Erro && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{etapa3Erro}</p>
                )}

                {/* Botão de Submissão */}
                <div className="flex justify-end pt-4 border-t">
                  <Button
                    type="button"
                    onClick={() => {
                      const erro = validarEtapa3();
                      setEtapa3Erro(erro);
                      if (!erro) setEtapaAtual(4);
                    }}
                    className="bg-brand-roxo hover:bg-brand-roxo/90 text-white py-2 px-6 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    disabled={isLoading}
                  >
                    Continuar para Termo
                    <CheckCircle2 size={16} />
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* ========== PASSO 4: Revisão + Aceite do Termo de Adesão ========== */}
          {etapaAtual === 4 && hasDgennyProposta && (
            <Card className="p-8 shadow-lg border-border rounded-2xl mb-6 relative overflow-hidden">
              <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-brand-roxo to-brand-rosa" aria-hidden />
              <RevisaoEAceiteEtapa
                token={tokenParam || ''}
                dealTerms={tokenData?.deal_terms || {}}
                modoAditivo={isAditivo}
                empresaHint={tokenData?.empresa_hint || null}
                customerData={customerData}
                responsavelOperacional={{
                  nome: responsavelOperacional.nome,
                  cpf: responsavelOperacional.cpf,
                  email: responsavelOperacional.email,
                  celular: responsavelOperacional.celular,
                  cargo: responsavelOperacional.cargo,
                }}
                responsavelFinanceiro={{
                  nome: responsavelFinanceiro.nome,
                  cpf: responsavelFinanceiro.cpf,
                  email: responsavelFinanceiro.email,
                  celular: responsavelFinanceiro.celular,
                  cargo: responsavelFinanceiro.cargo,
                }}
                operacionaisAdicionais={operacionaisAdicionais}
                financeirosAdicionais={financeirosAdicionais}
                uf={cnpjData?.estabelecimento?.estado?.sigla || ''}
                onBack={() => setEtapaAtual(3)}
                onAccepted={(adhesionId, { companyId }) => {
                  // sessionStorage como fallback robusto a F5; navigate state
                  // como canal primário (dados ricos sem precisar refetchar).
                  sessionStorage.setItem('adhesionId', adhesionId);
                  if (companyId) sessionStorage.setItem('companyId', companyId);
                  navigate('/cadastro-sucesso', {
                    state: {
                      adhesionId,
                      companyId,
                      hasDgennyProposta: true,
                      // Aditivo: a tela de sucesso muda o texto e não oferece
                      // contrato formal (o termo principal já cobre isso).
                      isAditivo,
                      nomeRecurso: isAditivo ? tokenData?.deal_terms?.produto_nome || '' : '',
                      // Snapshot dos dados que o ContratoFormalEtapa precisa,
                      // pra rota /contrato-formal/:adhesionId não ter que
                      // refetchar a Adhesion. Se F5 perde o state, a página
                      // mostra erro amigável (ver ContratoFormalPage).
                      socios: responsaveis,
                      responsavelOperacional: {
                        nome: responsavelOperacional.nome,
                        email: responsavelOperacional.email,
                      },
                      responsavelFinanceiro: {
                        nome: responsavelFinanceiro.nome,
                        email: responsavelFinanceiro.email,
                      },
                    },
                  });
                }}
              />
            </Card>
          )}

          <div className="text-center text-sm text-brand-azul/60 mt-6">
            <p>
              Ao prosseguir, você concorda com nossos <a href="/termos-de-uso" target="_blank" rel="noopener noreferrer" className="underline hover:text-brand-azul">Termos de Uso</a> e 
              {' '}<a href="/politica-de-privacidade" className="underline hover:text-brand-azul">Política de Privacidade</a>.
            </p>
            <p className="mt-2">
              Precisa de ajuda? <a href="#" className="text-brand-azul hover:underline">Fale conosco</a>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
